/**
 * ComputeEvaluator — safe recursive-descent expression evaluator.
 * Port of Flutter's CogoComputeEvaluator (805 LOC Dart → TypeScript).
 *
 * NO `new Function()`, NO `eval()`. Pure string parsing only.
 * Supports: literals, #path refs, ternary, comparison, arithmetic (with precedence),
 * array methods (filter/find/reduce/map/unique), Math.*, formatCurrency, predicates.
 */

import { ExpressionCache, type CacheStats, type CachedExpression } from "./expression-cache"

// Phase 471 D-471-04: monotonically-increasing counter for cuid() collision
// resistance. NON-cryptographic by design (consistent with randomId() — these
// are demo / client-side temp ids, NOT security tokens; SC#4 invariant).
let __cuidCounter = 0

export interface StateAccessor {
  get(path: string): unknown
  resolve(template: string): unknown
  getSnapshot?(): Record<string, unknown>
}

export class ComputeEvaluator {
  private state: StateAccessor
  private cache = new ExpressionCache()

  constructor(state: StateAccessor) {
    this.state = state
  }

  /** Return cache performance statistics. */
  getCacheStats(): CacheStats {
    return this.cache.getStats()
  }

  /** Clear all cached expression classifications. */
  clearCache(): void {
    this.cache.clear()
  }

  /** Main entry point — evaluate a compute expression string. */
  evaluate(expression: string): unknown {
    let trimmed = expression.trim()

    // 0. Unwrap #{...} wrapper if present (non-greedy: only match single #{...} pattern)
    const pureMatch = /^#\{([^}]+)\}$/.exec(trimmed)
    if (pureMatch) {
      trimmed = pureMatch[1].trim()
    }

    // Normalize optional chaining: ?. → . (SymbolStore.get already returns undefined for null intermediates)
    if (trimmed.includes("?.")) {
      trimmed = trimmed.replace(/\?\./g, ".")
    }

    // Cache check — skip regex chain on hit
    const cached = this.cache.get(trimmed)
    if (cached) {
      return this.executeCached(cached, trimmed)
    }

    return this.evaluateAndCache(trimmed)
  }

  /**
   * Execute a cached expression classification against current state.
   * The cache stores WHAT kind of expression it is, not the result value.
   */
  private executeCached(cached: CachedExpression, expr: string): unknown {
    switch (cached.type) {
      case "string-literal":
        return expr.substring(1, expr.length - 1)
      case "number-literal":
        return cached.value
      case "boolean":
        return cached.value
      case "null-literal":
        return null
      case "array-literal":
        return []
      case "object-literal":
        return {}
      case "simple-path":
        return this.state.get(expr.substring(1))
      case "dotted-path":
        return this.state.get(expr)
      case "length-compare": {
        const m = cached.meta as { path: string; op: string; val: number }
        const arr = this.state.get(m.path)
        const len = Array.isArray(arr) ? arr.length : 0
        return compareNumbers(len, m.val, m.op)
      }
      case "length-value": {
        const m = cached.meta as { path: string }
        const val = this.state.get(m.path)
        return Array.isArray(val) ? val.length : 0
      }
      case "simple-key": {
        const val = this.state.get(expr)
        if (val !== undefined) return val
        return this.state.resolve(expr)
      }
      default:
        // Complex or uncached expressions — re-evaluate fully
        return this.evaluateAndCache(expr)
    }
  }

  /**
   * Core evaluation logic — the original evaluate() body after unwrap.
   * Caches the expression classification at simple/common branches.
   */
  private evaluateAndCache(trimmed: string): unknown {
    // 0b. String literal: 'text' or "text"
    if (
      (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
      (trimmed.startsWith('"') && trimmed.endsWith('"'))
    ) {
      this.cache.set(trimmed, { type: "string-literal" })
      return trimmed.substring(1, trimmed.length - 1)
    }

    // 0c. Numeric literal
    const numLiteral = parseNumberStrict(trimmed)
    if (numLiteral !== null) {
      this.cache.set(trimmed, { type: "number-literal", value: numLiteral })
      return numLiteral
    }

    // 0d. Boolean / null literals
    if (trimmed === "true") {
      this.cache.set(trimmed, { type: "boolean", value: true })
      return true
    }
    if (trimmed === "false") {
      this.cache.set(trimmed, { type: "boolean", value: false })
      return false
    }
    if (trimmed === "null") {
      this.cache.set(trimmed, { type: "null-literal" })
      return null
    }

    // 0d3. Empty array literal: []
    if (trimmed === "[]") {
      this.cache.set(trimmed, { type: "array-literal", value: [] })
      return []
    }

    // 0d4. Empty object literal: {}
    if (trimmed === "{}") {
      this.cache.set(trimmed, { type: "object-literal", value: {} })
      return {}
    }

    // 0d5. Array literal with elements: [expr1, expr2, ...]
    // Only matches when the ENTIRE expression is wrapped in [...] (starts with [).
    // Array ACCESS like "path[0]" doesn't start with [ so won't match.
    // Excludes spread syntax [...] which is handled by the unique-values handler below.
    if (trimmed.startsWith("[") && trimmed.endsWith("]") && !trimmed.includes("...")) {
      const inner = trimmed.substring(1, trimmed.length - 1).trim()
      if (inner.length === 0) return []
      const elements = splitTopLevelCommas(inner)
      if (elements !== null) {
        return elements.map(el => this.evaluate(el.trim()))
      }
    }

    // 0d2. $-prefix data transform functions ($map, $filter, $sort, $find, $unique, $sum, $avg, $count, $groupBy)
    if (trimmed.startsWith("$")) {
      const dollarResult = this.tryDollarFunction(trimmed)
      if (dollarResult !== undefined) return dollarResult
    }

    // 0e. Number method: expr.toFixed(n) → string with n decimal places
    const toFixedMatch = /^(.+)\.toFixed\((\d+)\)$/.exec(trimmed)
    if (toFixedMatch) {
      const inner = this.evaluate(toFixedMatch[1].trim())
      const digits = parseInt(toFixedMatch[2], 10)
      return toNum(inner).toFixed(digits)
    }

    // 0f. String/Number method: expr.toString()
    const toStringMatch = /^(.+)\.toString\(\)$/.exec(trimmed)
    if (toStringMatch) {
      const inner = this.evaluate(toStringMatch[1].trim())
      return inner != null ? String(inner) : ""
    }

    // 0g. String methods: expr.toUpperCase(), expr.toLowerCase(), expr.trim()
    const stringNoArgMatch = /^(.+)\.(toUpperCase|toLowerCase|trim)\(\)$/.exec(trimmed)
    if (stringNoArgMatch) {
      const inner = this.evaluate(stringNoArgMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      switch (stringNoArgMatch[2]) {
        case "toUpperCase": return str.toUpperCase()
        case "toLowerCase": return str.toLowerCase()
        case "trim": return str.trim()
      }
    }

    // 0h. String methods with 1 arg: expr.includes('x'), expr.startsWith('x'), expr.endsWith('x')
    // Skip when expression contains "=>" (arrow function) — those are filter/find/map lambdas
    // handled by the array method handlers at step 4+.
    const stringOneArgMatch = /^(.+)\.(includes|startsWith|endsWith)\((.+)\)$/.exec(trimmed)
    if (stringOneArgMatch && !trimmed.includes("=>")) {
      const inner = this.evaluate(stringOneArgMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      const arg = this.evaluate(stringOneArgMatch[3].trim())
      const argStr = arg != null ? String(arg) : ""
      switch (stringOneArgMatch[2]) {
        case "includes": return str.includes(argStr)
        case "startsWith": return str.startsWith(argStr)
        case "endsWith": return str.endsWith(argStr)
      }
    }

    // 0i. String replace: expr.replace('a', 'b')
    const replaceMatch = /^(.+)\.replace\((.+),\s*(.+)\)$/.exec(trimmed)
    if (replaceMatch && !trimmed.includes("=>")) {
      const inner = this.evaluate(replaceMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      const search = this.evaluate(replaceMatch[2].trim())
      const replacement = this.evaluate(replaceMatch[3].trim())
      return str.replace(String(search ?? ""), String(replacement ?? ""))
    }

    // 0j. String substring: expr.substring(start, end)
    const substringMatch = /^(.+)\.substring\((\d+)(?:,\s*(\d+))?\)$/.exec(trimmed)
    if (substringMatch) {
      const inner = this.evaluate(substringMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      const start = parseInt(substringMatch[2], 10)
      const end = substringMatch[3] != null ? parseInt(substringMatch[3], 10) : undefined
      return str.substring(start, end)
    }

    // 0k. String split: expr.split('sep')
    const splitMatch = /^(.+)\.split\((.+)\)$/.exec(trimmed)
    if (splitMatch && !trimmed.includes("=>")) {
      const inner = this.evaluate(splitMatch[1].trim())
      const str = inner != null ? String(inner) : ""
      const sep = this.evaluate(splitMatch[2].trim())
      return str.split(String(sep ?? ""))
    }

    // 1. Simple state reference: "#path" (no spaces, parens, brackets, braces)
    if (
      trimmed.startsWith("#") &&
      !trimmed.includes(" ") &&
      !trimmed.includes("(") &&
      !trimmed.includes("[") &&
      !trimmed.includes("{")
    ) {
      this.cache.set(trimmed, { type: "simple-path" })
      return this.state.get(trimmed.substring(1))
    }

    // Simple dotted path like "computed.cartSubtotal" — resolve from state.
    // Exclude paths ending in .length (handled by length handler below).
    if (
      !trimmed.includes(" ") &&
      !trimmed.includes("(") &&
      !trimmed.includes("[") &&
      !trimmed.includes("{") &&
      !trimmed.includes("?") &&
      !trimmed.includes("'") &&
      /^[\w.]+$/.test(trimmed) &&
      trimmed.includes(".") &&
      !trimmed.endsWith(".length")
    ) {
      this.cache.set(trimmed, { type: "dotted-path" })
      return this.state.get(trimmed)
    }

    // 2. Length comparison: "#path.length == 0"
    const lengthCmp = /^#([\w.]+)\.length\s*(==|!=|>|<|>=|<=)\s*(\d+)$/.exec(trimmed)
    if (lengthCmp) {
      const arr = this.state.get(lengthCmp[1])
      const op = lengthCmp[2]
      const val = parseInt(lengthCmp[3], 10)
      const len = Array.isArray(arr) ? arr.length : 0
      this.cache.set(trimmed, { type: "length-compare", meta: { path: lengthCmp[1], op, val } })
      return compareNumbers(len, val, op)
    }

    // 2b. Length as value: "#path.length" or "path.length" (no comparison operator)
    const lengthVal = /^#?([\w.]+)\.length$/.exec(trimmed)
    if (lengthVal) {
      this.cache.set(trimmed, { type: "length-value", meta: { path: lengthVal[1] } })
      const val = this.state.get(lengthVal[1])
      if (Array.isArray(val)) return val.length
      if (typeof val === "string") return val.length
      return 0
    }

    // 3. Unique values extraction: ['prefix', ...new Set(#source.map(p => p.field))]
    const uniqueMatch =
      /\['([^']+)',\s*\.\.\.new Set\(#(\w+(?:\.\w+)*)\.map\(\w+\s*=>\s*\w+\.(\w+)\)\)/.exec(trimmed)
    if (uniqueMatch) {
      const prefix = uniqueMatch[1]
      const source = this.state.get(uniqueMatch[2])
      const field = uniqueMatch[3]
      if (Array.isArray(source)) {
        const valuesSet = new Set<string>()
        for (const item of source) {
          if (item && typeof item === "object") {
            const v = (item as Record<string, unknown>)[field]
            if (v != null && String(v).length > 0) {
              valuesSet.add(String(v))
            }
          }
        }
        const values = Array.from(valuesSet).sort()
        return [prefix, ...values]
      }
      return [prefix]
    }

    // 3b. Nullish coalescing: expr ?? fallback (checked early so find/filter chains can return null)
    {
      const nIdx = findTopLevelStr(trimmed, "??")
      if (nIdx > 0) {
        const leftExpr = trimmed.substring(0, nIdx).trim()
        const rightExpr = trimmed.substring(nIdx + 2).trim()
        // For simple paths, check state.get directly to detect null/undefined
        const pathLike = leftExpr.replace(/^#/, "")
        let leftVal: unknown
        if (/^[\w.]+$/.test(pathLike)) {
          leftVal = this.state.get(pathLike)
        } else {
          leftVal = this.evaluate(leftExpr)
        }
        if (leftVal === null || leftVal === undefined) {
          return this.evaluate(rightExpr)
        }
        return leftVal
      }
    }

    // 3c-pre. Ternary: expr ? valueA : valueB — must come BEFORE arithmetic split
    //         so "cond ? a + b : c" doesn't split on the + inside ternary branches.
    const ternaryResult = this.tryTernary(trimmed)
    if (ternaryResult !== undefined) return ternaryResult

    // 3c. Top-level arithmetic split (+, -) — must come BEFORE filter/find chains
    //     so "expr.filter(...).length + expr2" splits correctly.
    for (const op of ["+", "-"]) {
      const idx = findLastTopLevelChar(trimmed, op)
      if (idx > 0) {
        const left = this.evaluate(trimmed.substring(0, idx).trim())
        const right = this.evaluate(trimmed.substring(idx + 1).trim())
        // String concatenation via + (matching Flutter/Dart behavior)
        if (op === "+" && (typeof left === "string" || typeof right === "string")) {
          return `${left ?? ""}${right ?? ""}`
        }
        const ln = toNum(left), rn = toNum(right)
        return op === "+" ? ln + rn : ln - rn
      }
    }

    // 4. Array map — check FIRST because .map(x => { ... .find(...) ... })
    //    contains nested array methods that would be caught by later handlers
    const mapResult = this.tryMapExpression(trimmed)
    if (mapResult !== undefined) return mapResult

    // 5. Array filter: #source.filter(p => condition) or source.filter(...)
    //    With optional chaining: .filter(...).length, .filter(...).property
    const filterChainResult = this.tryFilterWithChain(trimmed)
    if (filterChainResult !== undefined) return filterChainResult

    // 5b. Array find: #source.find(p => condition).property or source.find(...)
    //    With optional chaining: .find(...).property
    const findChainResult = this.tryFindWithChain(trimmed)
    if (findChainResult !== undefined) return findChainResult

    // 5b2. Array every: #source.every(p => condition) → boolean  (Wave 11D.c)
    const everyResult = this.tryEveryExpression(trimmed)
    if (everyResult !== undefined) return everyResult

    // 5b3. Array some: #source.some(p => condition) → boolean  (Wave 11D.c)
    const someResult = this.trySomeExpression(trimmed)
    if (someResult !== undefined) return someResult

    // 5c. Array sort: #source.sort() with optional .join() chaining
    const sortResult = this.trySortExpression(trimmed)
    if (sortResult !== undefined) return sortResult

    // 5d. Array slice: #source.slice(start, end) or source.slice(start)
    const sliceResult = this.trySliceExpression(trimmed)
    if (sliceResult !== undefined) return sliceResult

    // 5e. Array join: #source.join('sep')
    const joinResult = this.tryJoinExpression(trimmed)
    if (joinResult !== undefined) return joinResult

    // 6a-pre. Object-building reduce: (source || []).reduce(... => {...}, {})
    const objReduceResult = this.tryObjectReduceExpression(trimmed)
    if (objReduceResult !== undefined) return objReduceResult

    // 6a. Array reduce
    const reduceResult = this.tryReduceExpression(trimmed)
    if (reduceResult !== undefined) return reduceResult

    // 6c. Logical AND/OR: expr1 && expr2, expr1 || expr2
    // Must come before ternary/comparison to correctly parse compound boolean expressions
    // like "product.price > 0 && product.originalPrice != null".
    const andIdx = findTopLevelStr(trimmed, "&&")
    if (andIdx > 0) {
      const left = this.evaluate(trimmed.substring(0, andIdx).trim())
      if (!isTruthy(left)) return left
      return this.evaluate(trimmed.substring(andIdx + 2).trim())
    }
    const orIdx = findTopLevelStr(trimmed, "||")
    if (orIdx > 0) {
      const left = this.evaluate(trimmed.substring(0, orIdx).trim())
      if (isTruthy(left)) return left
      return this.evaluate(trimmed.substring(orIdx + 2).trim())
    }

    // 6d. Negation: !expr  (after &&/|| so precedence is correct)
    if (trimmed.startsWith("!")) {
      const inner = trimmed.substring(1).trim()
      const result = this.evaluate(inner)
      return !isTruthy(result)
    }

    // 6e. Comparison: #a >= b, #a <= b, etc.
    const cmpResult = this.tryComparison(trimmed)
    if (cmpResult !== undefined) return cmpResult

    // 6. Math.max / Math.min / Math.round / Math.floor / Math.ceil / Math.abs / Math.pow
    const mathMatch = /^Math\.(max|min|round|floor|ceil|abs|pow)\((.+)\)$/.exec(trimmed)
    if (mathMatch) {
      const fn = mathMatch[1]
      const argsStr = mathMatch[2]
      const args = splitArgs(argsStr).map((a) => toNum(this.evaluate(a.trim())))
      switch (fn) {
        case "max":
          return args.length >= 2 ? Math.max(args[0], args[1]) : (args[0] ?? 0)
        case "min":
          return args.length >= 2 ? Math.min(args[0], args[1]) : (args[0] ?? 0)
        case "round":
          return args.length > 0 ? Math.round(args[0]) : 0
        case "floor":
          return args.length > 0 ? Math.floor(args[0]) : 0
        case "ceil":
          return args.length > 0 ? Math.ceil(args[0]) : 0
        case "abs":
          return args.length > 0 ? Math.abs(args[0]) : 0
        case "pow":
          return Math.pow(args[0] ?? 0, args[1] ?? 0)
      }
    }

    // 6g. today(format?) — current date formatted. Default 'YYYY-MM-DD'.
    //     Patterns: YYYY, MM, DD, YYYY-MM, YYYYMM, YYYY-MM-DD, YYYYMMDD
    const todayMatch = /^today\((?:'([^']*)'|"([^"]*)")?\)$/.exec(trimmed)
    if (todayMatch) {
      const fmt = todayMatch[1] ?? todayMatch[2] ?? "YYYY-MM-DD"
      return formatDatePattern(new Date(), fmt)
    }

    // 6h. monthOffset(n, format?) — month offset from today, formatted.
    //     monthOffset(0, 'YYYY-MM') → current month, monthOffset(-1, 'YYYYMM') → last month
    const monthOffsetMatch = /^monthOffset\((.+)\)$/.exec(trimmed)
    if (monthOffsetMatch) {
      const args = splitArgs(monthOffsetMatch[1])
      const offset = toNum(this.evaluate(args[0].trim()))
      const fmt = args.length > 1
        ? String(this.evaluate(args[1].trim()))
        : "YYYY-MM"
      const d = new Date()
      d.setMonth(d.getMonth() + offset)
      return formatDatePattern(d, fmt)
    }

    // 6i. randomId() — short pseudo-unique id (~9 base36 chars).
    //     Not cryptographically random — by design (demo / client-side temp ids).
    const randomIdMatch = /^randomId\(\)$/.exec(trimmed)
    if (randomIdMatch) {
      return Math.random().toString(36).slice(2, 11)
    }

    // 6i2. cuid() — collision-resistant non-empty id string.
    //      Phase 471 D-471-04: closes a pre-existing ontology↔runtime gap
    //      (declared in expression-accessors-v1.ttl, unimplemented in v1+v2).
    //      NON-cryptographic by design (timestamp + base36 + counter form) —
    //      consistent with randomId() non-crypto invariant (SC#4). NOT a token.
    const cuidMatch = /^cuid\(\)$/.exec(trimmed)
    if (cuidMatch) {
      __cuidCounter = (__cuidCounter + 1) % 0x7fffffff
      return (
        "c" +
        Date.now().toString(36) +
        __cuidCounter.toString(36) +
        Math.random().toString(36).slice(2, 8)
      )
    }

    // 6i3. concat(a, b, ...) — string concatenation of stringified args.
    //      Phase 471 D-471-04: closes the same pre-existing ontology↔runtime gap.
    const concatMatch = /^concat\((.*)\)$/.exec(trimmed)
    if (concatMatch) {
      const argsStr = concatMatch[1].trim()
      if (argsStr === "") return ""
      const parts = splitArgs(argsStr).map((a) => {
        const v = this.evaluate(a.trim())
        return v === null || v === undefined ? "" : String(v)
      })
      return parts.join("")
    }

    // 7. formatCurrency(expr) -> "$X.XX"
    const currencyMatch = /^formatCurrency\((.+)\)$/.exec(trimmed)
    if (currencyMatch) {
      const inner = currencyMatch[1].trim()
      const value = toNum(this.evaluate(inner))
      return `$${value.toFixed(2)}`
    }

    // 7b. formatCrypto(rawAmount, decimals, symbol?) -> "1.5 ETH"
    const formatCryptoMatch = /^formatCrypto\((.+)\)$/.exec(trimmed)
    if (formatCryptoMatch) {
      const args = splitArgs(formatCryptoMatch[1])
      const raw = toNum(this.evaluate(args[0].trim()))
      const decimals = args.length > 1 ? toNum(this.evaluate(args[1].trim())) : 0
      const symbol = args.length > 2 ? String(this.evaluate(args[2].trim())) : ""
      const converted = decimals > 0 ? raw / Math.pow(10, decimals) : raw
      const maxDp = Math.min(decimals || 8, 8)
      const formatted = converted.toFixed(maxDp).replace(/\.?0+$/, "")
      return symbol ? `${formatted} ${symbol}` : formatted
    }

    // 7c. fromWei(value, decimals?) -> number
    const fromWeiMatch = /^fromWei\((.+)\)$/.exec(trimmed)
    if (fromWeiMatch) {
      const args = splitArgs(fromWeiMatch[1])
      const wei = toNum(this.evaluate(args[0].trim()))
      const decimals = args.length > 1 ? toNum(this.evaluate(args[1].trim())) : 18
      return wei / Math.pow(10, decimals)
    }

    // 7d. toWei(amount, decimals?) -> string
    const toWeiMatch = /^toWei\((.+)\)$/.exec(trimmed)
    if (toWeiMatch) {
      const args = splitArgs(toWeiMatch[1])
      const amount = toNum(this.evaluate(args[0].trim()))
      const decimals = args.length > 1 ? toNum(this.evaluate(args[1].trim())) : 18
      return String(Math.round(amount * Math.pow(10, decimals)))
    }

    // 7e. shortenAddress(address, chars?) -> "0x1234...5678"
    const shortenMatch = /^shortenAddress\((.+)\)$/.exec(trimmed)
    if (shortenMatch) {
      const args = splitArgs(shortenMatch[1])
      const addr = String(this.evaluate(args[0].trim()) ?? "")
      const chars = args.length > 1 ? toNum(this.evaluate(args[1].trim())) : 4
      if (addr.length <= chars * 2 + 2) return addr
      return `${addr.substring(0, chars + 2)}...${addr.substring(addr.length - chars)}`
    }

    // 7f. formatNumber(n, decimals?) -> "1,234,567.89"
    const formatNumMatch = /^formatNumber\((.+)\)$/.exec(trimmed)
    if (formatNumMatch) {
      const args = splitArgs(formatNumMatch[1])
      const val = toNum(this.evaluate(args[0].trim()))
      if (args.length > 1) {
        const decimals = toNum(this.evaluate(args[1].trim()))
        return val.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
      }
      return val.toLocaleString("en-US")
    }

    // 7g. timeAgo(timestamp) -> "3 min ago"
    const timeAgoMatch = /^timeAgo\((.+)\)$/.exec(trimmed)
    if (timeAgoMatch) {
      const ts = toNum(this.evaluate(timeAgoMatch[1].trim()))
      const now = Date.now() / 1000
      const diff = Math.abs(now - ts)
      if (diff < 60) return "just now"
      if (diff < 3600) return `${Math.floor(diff / 60)} min ago`
      if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`
      if (diff < 2592000) return `${Math.floor(diff / 86400)} day ago`
      return `${Math.floor(diff / 2592000)} mo ago`
    }

    // 8. Multiplication/division/modulo (higher precedence, handled after +/- at step 3c).
    for (const op of ["*", "/", "%"]) {
      const idx = findLastTopLevelChar(trimmed, op)
      if (idx > 0) {
        const left = this.evaluate(trimmed.substring(0, idx).trim())
        const right = this.evaluate(trimmed.substring(idx + 1).trim())
        const ln = toNum(left), rn = toNum(right)
        if (op === "*") return ln * rn
        if (op === "/") return rn !== 0 ? ln / rn : 0
        return rn !== 0 ? ln % rn : 0
      }
    }

    // 8b. Parenthesized expression: (expr) — strip parens and recurse.
    if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
      // Verify the closing paren matches the opening one (not nested)
      let depth = 0
      let balanced = true
      for (let i = 0; i < trimmed.length - 1; i++) {
        if (trimmed[i] === "(") depth++
        if (trimmed[i] === ")") depth--
        if (depth === 0) { balanced = false; break }
      }
      if (balanced) {
        return this.evaluate(trimmed.substring(1, trimmed.length - 1))
      }
    }

    // 8c. Array index access: path[0], path[0].property, path[index].sub[1].prop
    if (trimmed.includes("[")) {
      const result = this.resolveArrayAccess(trimmed)
      if (result !== undefined) return result
    }

    // 9. Simple key (no dots, no spaces, no special chars) — state lookup
    if (/^\w+$/.test(trimmed)) {
      const val = this.state.get(trimmed)
      if (val !== undefined) {
        this.cache.set(trimmed, { type: "simple-key" })
        return val // return null for explicitly-null values
      }
    }

    // 10. Fall back to resolve (handles #{path} and interpolation)
    return this.state.resolve(trimmed)
  }

  // ---- $-prefix Data Transform Functions ----

  /**
   * Try to parse and execute a $-prefix function call.
   * Returns undefined if the expression is not a valid $-function.
   */
  private tryDollarFunction(expr: string): unknown {
    const match = /^\$(\w+)\((.+)\)$/s.exec(expr)
    if (!match) return undefined
    const funcName = match[1]
    const argsStr = match[2]
    switch (funcName) {
      case "map": return this.dollarMap(argsStr)
      case "filter": return this.dollarFilter(argsStr)
      case "sort": return this.dollarSort(argsStr)
      case "find": return this.dollarFind(argsStr)
      case "unique": return this.dollarUnique(argsStr)
      case "sum": return this.dollarSum(argsStr)
      case "avg": return this.dollarAvg(argsStr)
      case "count": return this.dollarCount(argsStr)
      case "groupBy": return this.dollarGroupBy(argsStr)
      default: return undefined
    }
  }

  /** $map(source, 'predicate') — map each item through an expression. */
  private dollarMap(argsStr: string): unknown[] {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source)) return []
    const predicate = stripQuotes(args[1]?.trim() ?? "")
    const hasOperator = /[+\-*/%]/.test(predicate)

    return source.map((item) => {
      const itemMap = toStringKeyMap(item)
      if (!hasOperator) {
        // Simple field extraction
        return getNestedValue(itemMap, predicate)
      }
      // Arithmetic predicate: substitute field references with values, then evaluate
      let resolved = predicate
      for (const key of Object.keys(itemMap)) {
        const re = new RegExp(`\\b${key}\\b`, "g")
        resolved = resolved.replace(re, String(itemMap[key]))
      }
      const result = this.evaluate(resolved)
      return Number(result)
    })
  }

  /** $filter(source, 'condition') — keep items matching condition. */
  private dollarFilter(argsStr: string): unknown[] {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source)) return []
    const rawCondition = stripQuotes(args[1]?.trim() ?? "")
    const paramName = "_$item"
    // Prefix bare identifiers with paramName so evalFilterCondition can resolve them
    const condition = prefixBareFields(rawCondition, paramName)
    return source.filter((item) => this.evalFilterCondition(condition, paramName, item))
  }

  /** $sort(source, 'field', 'direction?') — sort by field asc/desc. */
  private dollarSort(argsStr: string): unknown[] {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source) || source.length === 0) return []
    const field = stripQuotes(args[1]?.trim() ?? "")
    const direction = stripQuotes(args[2]?.trim() ?? "asc")
    const sorted = [...source].sort((a, b) => {
      const aVal = getNestedValue(toStringKeyMap(a), field)
      const bVal = getNestedValue(toStringKeyMap(b), field)
      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "desc" ? bVal - aVal : aVal - bVal
      }
      const cmp = String(aVal ?? "").localeCompare(String(bVal ?? ""))
      return direction === "desc" ? -cmp : cmp
    })
    return sorted
  }

  /** $find(source, 'condition') — return first matching item or null. */
  private dollarFind(argsStr: string): unknown {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source)) return null
    const rawCondition = stripQuotes(args[1]?.trim() ?? "")
    const paramName = "_$item"
    const condition = prefixBareFields(rawCondition, paramName)
    return source.find((item) => this.evalFilterCondition(condition, paramName, item)) ?? null
  }

  /** $unique(source, 'field?') — deduplicate array. */
  private dollarUnique(argsStr: string): unknown[] {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source)) return []
    if (args.length < 2 || !args[1]?.trim()) {
      return [...new Set(source)]
    }
    const field = stripQuotes(args[1].trim())
    const seen = new Set<unknown>()
    const result: unknown[] = []
    for (const item of source) {
      const val = getNestedValue(toStringKeyMap(item), field)
      if (!seen.has(val)) {
        seen.add(val)
        result.push(item)
      }
    }
    return result
  }

  /** $sum(source, 'field') — sum numeric field values. */
  private dollarSum(argsStr: string): number {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source) || source.length === 0) return 0
    const field = stripQuotes(args[1]?.trim() ?? "")
    return source.reduce((sum: number, item) => {
      const val = getNestedValue(toStringKeyMap(item), field)
      return sum + (typeof val === "number" ? val : 0)
    }, 0)
  }

  /** $avg(source, 'field') — average of numeric field values. */
  private dollarAvg(argsStr: string): number {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source) || source.length === 0) return 0
    const field = stripQuotes(args[1]?.trim() ?? "")
    const total = source.reduce((sum: number, item) => {
      const val = getNestedValue(toStringKeyMap(item), field)
      return sum + (typeof val === "number" ? val : 0)
    }, 0)
    return total / source.length
  }

  /** $count(source, 'condition?') — count items, optionally with predicate. */
  private dollarCount(argsStr: string): number {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source)) return 0
    if (args.length < 2 || !args[1]?.trim()) {
      return source.length
    }
    const rawCondition = stripQuotes(args[1].trim())
    const paramName = "_$item"
    const condition = prefixBareFields(rawCondition, paramName)
    return source.filter((item) => this.evalFilterCondition(condition, paramName, item)).length
  }

  /** $groupBy(source, 'field') — group items by field value. */
  private dollarGroupBy(argsStr: string): Record<string, unknown[]> {
    const args = splitArgs(argsStr)
    const source = this.evaluate(args[0].trim())
    if (!Array.isArray(source) || source.length === 0) return {}
    const field = stripQuotes(args[1]?.trim() ?? "")
    const groups: Record<string, unknown[]> = {}
    for (const item of source) {
      const key = String(getNestedValue(toStringKeyMap(item), field) ?? "")
      if (!groups[key]) groups[key] = []
      groups[key].push(item)
    }
    return groups
  }

  // ---- Filter Condition ----

  /** Evaluate a filter condition against an item. */
  evalFilterCondition(condition: string, paramName: string, item: unknown): boolean {
    const itemMap =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {}

    // Split by && (top level)
    if (condition.includes("&&")) {
      const parts = splitLogicalParts(condition, "&&")
      if (parts) {
        return (
          this.evalFilterCondition(parts[0], paramName, item) &&
          this.evalFilterCondition(parts[1], paramName, item)
        )
      }
    }

    // Split by || (top level)
    if (condition.includes("||")) {
      const parts = splitLogicalParts(condition, "||")
      if (parts) {
        return (
          this.evalFilterCondition(parts[0], paramName, item) ||
          this.evalFilterCondition(parts[1], paramName, item)
        )
      }
    }

    let expr = condition.trim()

    // Remove outer parens
    while (expr.startsWith("(") && expr.endsWith(")") && matchingParen(expr)) {
      expr = expr.substring(1, expr.length - 1).trim()
    }

    // Recursion with simplified expr
    if (expr !== condition.trim()) {
      return this.evalFilterCondition(expr, paramName, item)
    }

    // Negation: !expr
    if (expr.startsWith("!")) {
      const inner = expr.substring(1).trim()
      if (inner.startsWith("#")) {
        const val = this.state.get(inner.substring(1))
        return !isTruthy(val)
      }
      return !this.evalFilterCondition(inner, paramName, item)
    }

    // .includes() pattern: a.toLowerCase().includes(b.toLowerCase())
    const includesMatch =
      /(\w+\.\w+(?:\.\w+)*)\.toLowerCase\(\)\.includes\((.+)\.toLowerCase\(\)\)/.exec(expr)
    if (includesMatch) {
      const leftPath = includesMatch[1]
      const rightPath = includesMatch[2]
      const leftVal = this.resolveFilterValue(leftPath, paramName, itemMap)
      const rightVal = this.resolveFilterValue(rightPath, paramName, itemMap)
      const leftStr = (leftVal ?? "").toString().toLowerCase()
      const rightStr = (rightVal ?? "").toString().toLowerCase()
      return leftStr.includes(rightStr)
    }

    // Comparison: a === b, a !== b, a == b, a != b, a >= b, a <= b, a > b, a < b
    for (const op of ["===", "!==", ">=", "<=", "==", "!=", ">", "<"]) {
      const opIdx = expr.indexOf(op)
      if (opIdx > 0) {
        const leftStr = expr.substring(0, opIdx).trim()
        const rightStr = expr.substring(opIdx + op.length).trim()
        const left = this.resolveFilterValue(leftStr, paramName, itemMap)
        const right = this.resolveFilterValue(rightStr, paramName, itemMap)
        if (op === "===" || op === "==") return left === right || String(left) === String(right)
        if (op === "!==" || op === "!=") return left !== right && String(left) !== String(right)
        // Use numeric comparison when both sides are numbers; otherwise string comparison
        const numL = typeof left === "number" ? left : parseFloat(String(left))
        const numR = typeof right === "number" ? right : parseFloat(String(right))
        const bothNumeric = !isNaN(numL) && !isNaN(numR)
        if (op === ">=") return bothNumeric ? numL >= numR : String(left) >= String(right)
        if (op === "<=") return bothNumeric ? numL <= numR : String(left) <= String(right)
        if (op === ">") return bothNumeric ? numL > numR : String(left) > String(right)
        if (op === "<") return bothNumeric ? numL < numR : String(left) < String(right)
      }
    }

    // Truthy check for state ref: #path
    if (expr.startsWith("#")) {
      return isTruthy(this.state.get(expr.substring(1)))
    }

    // Truthy check for item property: paramName.prop
    if (expr.startsWith(`${paramName}.`)) {
      const prop = expr.substring(paramName.length + 1)
      const val = getNestedValue(itemMap, prop)
      return isTruthy(val)
    }

    return true // default pass
  }

  /** Resolve a value in filter context: handles item props, state refs, and literals. */
  resolveFilterValue(
    path: string,
    paramName: string,
    itemMap: Record<string, unknown>,
  ): unknown {
    const trimmed = path.trim()

    // Quoted string literal
    if (
      (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
      (trimmed.startsWith('"') && trimmed.endsWith('"'))
    ) {
      let str = trimmed.substring(1, trimmed.length - 1)
      // Resolve #{...} state refs embedded inside string literals
      // e.g. '#{_system.navigation.params.id}' → actual param value
      if (str.includes("#{")) {
        str = str.replace(/#\{([^}]+)\}/g, (_, ref) => {
          const val = this.state.get((ref as string).trim())
          return val != null ? String(val) : ""
        })
      }
      return str
    }

    // Number literal
    const asNum = parseNumberStrict(trimmed)
    if (asNum !== null) return asNum

    // State reference: #path
    if (trimmed.startsWith("#")) {
      return this.state.get(trimmed.substring(1))
    }

    // Item property: paramName.prop
    if (trimmed.startsWith(`${paramName}.`)) {
      const prop = trimmed.substring(paramName.length + 1)
      return getNestedValue(itemMap, prop)
    }

    // State path without # prefix: dotted paths that exist in state
    if (trimmed.includes(".") && /^[a-zA-Z_]\w*\./.test(trimmed)) {
      const val = this.state.get(trimmed)
      if (val !== undefined) return val
    }

    return trimmed
  }

  // ---- Filter with Chain ----

  /**
   * Parse filter expressions with optional chaining:
   *   #source.filter(p => condition)            → returns array
   *   #source.filter(p => condition).length     → returns count
   *   #source.filter(p => condition).property   → returns property of first match
   */
  // ---- Every / Some (Wave 11D.c — ported verbatim from cogo-runtime-core,
  //      v1 stateGet() call-site adapted to v2 canonical this.state.get()) ----

  /**
   * Parse every expressions: #source.every(p => condition) → boolean.
   * Vacuous-true on empty array; false when source is not an array. No chaining.
   */
  private tryEveryExpression(expr: string): unknown {
    const everyStart = expr.search(/\.every\(/)
    if (everyStart < 0) return undefined

    const sourcePath = expr.substring(0, everyStart).replace(/^#/, "")
    const afterEvery = expr.substring(everyStart + 7) // skip ".every("

    let depth = 1
    let i = 0
    for (; i < afterEvery.length && depth > 0; i++) {
      if (afterEvery[i] === "(") depth++
      if (afterEvery[i] === ")") depth--
    }
    if (depth !== 0) return undefined

    const body = afterEvery.substring(0, i - 1)
    const afterClose = afterEvery.substring(i)
    // Only support bare `.every(p => cond)` — reject unknown chaining
    if (afterClose !== "" && afterClose.trim() !== "") return undefined

    const lambdaMatch = /^(\w+)\s*=>\s*(.+)$/.exec(body.trim())
    if (!lambdaMatch) return undefined

    const paramName = lambdaMatch[1]
    const condition = lambdaMatch[2]
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return false

    return source.every((item) => isTruthy(this.evalLambdaCondition(condition, paramName, item)))
  }

  /**
   * Parse some expressions: #source.some(p => condition) → boolean.
   * Returns false when the source is empty or not an array. No chaining supported.
   */
  private trySomeExpression(expr: string): unknown {
    const someStart = expr.search(/\.some\(/)
    if (someStart < 0) return undefined

    const sourcePath = expr.substring(0, someStart).replace(/^#/, "")
    const afterSome = expr.substring(someStart + 6) // skip ".some("

    let depth = 1
    let i = 0
    for (; i < afterSome.length && depth > 0; i++) {
      if (afterSome[i] === "(") depth++
      if (afterSome[i] === ")") depth--
    }
    if (depth !== 0) return undefined

    const body = afterSome.substring(0, i - 1)
    const afterClose = afterSome.substring(i)
    if (afterClose !== "" && afterClose.trim() !== "") return undefined

    const lambdaMatch = /^(\w+)\s*=>\s*(.+)$/.exec(body.trim())
    if (!lambdaMatch) return undefined

    const paramName = lambdaMatch[1]
    const condition = lambdaMatch[2]
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return false

    return source.some((item) => isTruthy(this.evalLambdaCondition(condition, paramName, item)))
  }

  /**
   * Wave 11D.c — evaluate a boolean lambda body against a single item, supporting
   * both primitive items (via bare `paramName` reference, e.g. `x > 0`) and
   * object items (via `paramName.prop`). Delegates to `evalFilterCondition`
   * after rewriting bare `paramName` tokens to a synthetic `paramName.__value__`.
   */
  private evalLambdaCondition(
    condition: string,
    paramName: string,
    item: unknown,
  ): boolean {
    // If item is an object, normal filter condition semantics apply.
    if (item !== null && typeof item === "object") {
      return this.evalFilterCondition(condition, paramName, item)
    }
    // Primitive item: rewrite bare paramName → paramName.__value__ and wrap.
    const bareParamRe = new RegExp(`\\b${paramName}\\b(?!\\.)`, "g")
    const rewritten = condition.replace(bareParamRe, `${paramName}.__value__`)
    return this.evalFilterCondition(rewritten, paramName, { __value__: item })
  }

  private tryFilterWithChain(expr: string): unknown {
    // Find .filter( with proper paren matching
    const filterStart = expr.search(/\.filter\(/)
    if (filterStart < 0) return undefined

    const sourcePath = expr.substring(0, filterStart).replace(/^#/, "")
    const afterFilter = expr.substring(filterStart + 8) // skip ".filter("

    // Find matching closing paren for .filter(...)
    let depth = 1
    let i = 0
    for (; i < afterFilter.length && depth > 0; i++) {
      if (afterFilter[i] === "(") depth++
      if (afterFilter[i] === ")") depth--
    }
    if (depth !== 0) return undefined

    const filterBody = afterFilter.substring(0, i - 1) // content inside filter()
    const afterClose = afterFilter.substring(i) // everything after the closing paren

    // Parse the lambda: paramName => condition
    const lambdaMatch = /^(\w+)\s*=>\s*(.+)$/.exec(filterBody.trim())
    if (!lambdaMatch) return undefined

    const paramName = lambdaMatch[1]
    const condition = lambdaMatch[2]
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) {
      // If chaining .length, return 0; otherwise return []
      if (afterClose.startsWith(".length")) return 0
      return []
    }

    const filtered = source.filter((item) => this.evalFilterCondition(condition, paramName, item))

    // Check for chaining after filter()
    if (afterClose === "" || afterClose.trim() === "") return filtered
    if (afterClose.startsWith(".length")) return filtered.length
    if (afterClose.startsWith(".")) {
      const prop = afterClose.substring(1)
      if (filtered.length > 0 && typeof filtered[0] === "object") {
        return getNestedValue(filtered[0] as Record<string, unknown>, prop) ?? null
      }
      return null
    }

    return filtered
  }

  // ---- Find with Chain ----

  /**
   * Parse find expressions with optional chaining:
   *   #source.find(p => condition)              → returns found item or null
   *   #source.find(p => condition).property     → returns property of found item
   */
  private tryFindWithChain(expr: string): unknown {
    const findStart = expr.search(/\.find\(/)
    if (findStart < 0) return undefined

    const sourcePath = expr.substring(0, findStart).replace(/^#/, "")
    const afterFind = expr.substring(findStart + 6) // skip ".find("

    let depth = 1
    let i = 0
    for (; i < afterFind.length && depth > 0; i++) {
      if (afterFind[i] === "(") depth++
      if (afterFind[i] === ")") depth--
    }
    if (depth !== 0) return undefined

    const findBody = afterFind.substring(0, i - 1)
    const afterClose = afterFind.substring(i)

    const lambdaMatch = /^(\w+)\s*=>\s*(.+)$/.exec(findBody.trim())
    if (!lambdaMatch) return undefined

    const paramName = lambdaMatch[1]
    const condition = lambdaMatch[2]
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return null

    const found = source.find((item) => this.evalFilterCondition(condition, paramName, item)) ?? null

    // Check for property chaining after find()
    if (afterClose === "" || afterClose.trim() === "") return found
    if (afterClose.startsWith(".") && found && typeof found === "object") {
      const prop = afterClose.substring(1)
      return getNestedValue(found as Record<string, unknown>, prop) ?? null
    }

    return found
  }

  // ---- Sort ----

  private trySortExpression(expr: string): unknown {
    // #source.sort() with optional .join('sep')
    const sortMatch = /^#?(\w+(?:\.\w+)*)\.sort\(\)(.*)$/.exec(expr)
    if (!sortMatch) return undefined

    const source = this.state.get(sortMatch[1])
    if (!Array.isArray(source)) return []

    const sorted = [...source].sort((a, b) => String(a).localeCompare(String(b)))
    const afterSort = sortMatch[2].trim()

    // Check for .join() chaining
    const joinChainMatch = /^\.join\((.+)\)$/.exec(afterSort)
    if (joinChainMatch) {
      const sep = this.evaluate(joinChainMatch[1].trim())
      return sorted.join(String(sep ?? ""))
    }

    if (afterSort === "") return sorted
    return sorted
  }

  // ---- Slice ----

  private trySliceExpression(expr: string): unknown {
    // source.slice(start) or source.slice(start, end)
    const sliceStart = expr.search(/\.slice\(/)
    if (sliceStart === -1) return undefined

    const sourcePath = expr.substring(0, sliceStart).replace(/^#/, "")
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return undefined

    const afterSlice = expr.substring(sliceStart + 7) // skip ".slice("
    // Find matching closing paren
    let depth = 1
    let i = 0
    for (; i < afterSlice.length && depth > 0; i++) {
      if (afterSlice[i] === "(") depth++
      if (afterSlice[i] === ")") depth--
    }
    if (depth !== 0) return undefined

    const argsStr = afterSlice.substring(0, i - 1).trim()
    const args = argsStr.split(",").map((a) => toNum(this.evaluate(a.trim())))
    const start = typeof args[0] === "number" ? args[0] : 0
    const end = args.length > 1 && typeof args[1] === "number" ? args[1] : undefined

    return source.slice(start, end)
  }

  // ---- Join ----

  private tryJoinExpression(expr: string): unknown {
    const joinMatch = /^#?(\w+(?:\.\w+)*)\.join\((.+)\)$/.exec(expr)
    if (!joinMatch) return undefined

    const source = this.state.get(joinMatch[1])
    if (!Array.isArray(source)) return ""

    const sep = this.evaluate(joinMatch[2].trim())
    return source.join(String(sep ?? ""))
  }

  /**
   * Evaluate a predicate string like "item => item.id == #{product.id}".
   * Used by ActionFlowEngine for removeWhere, updateWhere, upsertWhere match predicates.
   */
  evalPredicate(
    predicate: string,
    item: unknown,
    context?: Record<string, unknown>,
  ): boolean {
    const trimmed = predicate.trim()

    // Parse "paramName => expression"
    const arrowMatch = /^(\w+)\s*=>\s*(.+)$/.exec(trimmed)
    if (!arrowMatch) {
      // No arrow function syntax — detect implicit item reference.
      // Predicates like "item.id === 'coffee-1'" use "item" as implicit paramName.
      // Extract the first dot-path identifier and use it as paramName for evalFilterCondition.
      const implicitParam = /^(\w+)\./.exec(trimmed)
      if (implicitParam) {
        return this.evalFilterCondition(trimmed, implicitParam[1], item)
      }
      // Bare property predicates: "id === 'p1'" where "id" is a direct item property.
      // Prefix bare identifiers with a synthetic param to route through evalFilterCondition.
      const itemMap = item && typeof item === "object" ? item as Record<string, unknown> : {}
      const bareIdMatch = /^(\w+)\s*(===|!==|==|!=|>=|<=|>|<)\s*(.+)$/.exec(trimmed)
      if (bareIdMatch && bareIdMatch[1] in itemMap) {
        const syntheticParam = "_item"
        const prefixed = `${syntheticParam}.${bareIdMatch[1]} ${bareIdMatch[2]} ${bareIdMatch[3]}`
        return this.evalFilterCondition(prefixed, syntheticParam, item)
      }
      // Fallback: evaluate as a raw expression (already resolved)
      return isTruthy(this.evaluate(trimmed))
    }

    const paramName = arrowMatch[1]
    let body = arrowMatch[2].trim()

    // Resolve #{...} patterns from context/state
    body = body.replace(/#\{([^}]+)\}/g, (_, ref) => {
      const r = (ref as string).trim()
      // Try context first
      if (context) {
        const ctxVal = getNestedValue(context, r)
        if (ctxVal !== undefined) {
          if (typeof ctxVal === "string") return JSON.stringify(ctxVal)
          return String(ctxVal)
        }
      }
      // Fall back to state
      const stateVal = this.state.get(r)
      if (stateVal === null || stateVal === undefined) return "null"
      if (typeof stateVal === "string") return JSON.stringify(stateVal)
      return String(stateVal)
    })

    // Now evaluate using filter condition logic
    return this.evalFilterCondition(body, paramName, item)
  }

  // ---- Object-building Reduce ----

  /**
   * Handles expressions like:
   *   (source || []).reduce((acc, item) => { acc[item.key] = item.val; return acc; }, {})
   *   (source || []).reduce((acc, item) => ({ ...acc, [item.key]: item.val }), {})
   * Returns Record<string, string> or undefined if not an object-reduce pattern.
   * Port of Dart _tryObjectReduceExpression from Phase 213.
   */
  private tryObjectReduceExpression(expr: string): Record<string, string> | undefined {
    // Try (source || []).reduce( pattern first (fallback array)
    const fallbackMatch = /^\(#?([\w.]+)\s*\|\|\s*\[\]\)\.reduce\(\((\w+),\s*(\w+)\)\s*=>\s*/.exec(expr)

    // Also try #source.path.reduce( for direct object reduce
    const directMatch = fallbackMatch
      ? null
      : /^#?(\w+(?:\.\w+)*)\.reduce\(\((\w+),\s*(\w+)\)\s*=>\s*/.exec(expr)

    const match = fallbackMatch ?? directMatch
    if (!match) return undefined

    const sourcePath = match[1].replace(/^#/, "").trim()
    const accName = match[2]
    const itemName = match[3]
    const rest = expr.substring(match[0].length)

    // Find the matching closing paren for .reduce(...) using depth tracking
    let depth = 1
    let reduceCloseIdx = -1
    for (let i = 0; i < rest.length; i++) {
      if (rest[i] === "(") depth++
      if (rest[i] === ")") {
        depth--
        if (depth === 0) {
          reduceCloseIdx = i
          break
        }
      }
    }
    if (reduceCloseIdx < 0) return undefined
    const inner = rest.substring(0, reduceCloseIdx)

    // Split body from initial value at last top-level comma
    const lastComma = findLastTopLevelComma(inner)
    if (lastComma < 0) return undefined

    const body = inner.substring(0, lastComma).trim()
    const initialStr = inner.substring(lastComma + 1).trim()

    // Only handle object-building: initial value must be {}
    if (initialStr !== "{}") return undefined

    // Resolve source from state
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return {}

    // Detect key and value fields from body
    let keyField: string | null = null
    let valueField: string | null = null

    // Pattern A (imperative): acc[item.code1] = item.description1; return acc;
    const imperativeRe = new RegExp(
      escapeRegExp(accName) +
        "\\[" +
        escapeRegExp(itemName) +
        "\\.(\\w+)\\]\\s*=\\s*" +
        escapeRegExp(itemName) +
        "\\.(\\w+)"
    )
    const imperativeMatch = imperativeRe.exec(body)
    if (imperativeMatch) {
      keyField = imperativeMatch[1]
      valueField = imperativeMatch[2]
    }

    // Pattern B (spread): { ...acc, [item.code1]: item.description1 }
    if (!keyField) {
      const spreadRe = new RegExp(
        "\\[" +
          escapeRegExp(itemName) +
          "\\.(\\w+)\\]\\s*:\\s*" +
          escapeRegExp(itemName) +
          "\\.(\\w+)"
      )
      const spreadMatch = spreadRe.exec(body)
      if (spreadMatch) {
        keyField = spreadMatch[1]
        valueField = spreadMatch[2]
      }
    }

    if (!keyField || !valueField) return undefined

    // Build the result map
    const result: Record<string, string> = {}
    for (const item of source) {
      if (item && typeof item === "object") {
        const k = String((item as Record<string, unknown>)[keyField] ?? "")
        const v = String((item as Record<string, unknown>)[valueField] ?? "")
        if (k) {
          result[k] = v
        }
      }
    }
    return result
  }

  // ---- Reduce ----

  private tryReduceExpression(expr: string): unknown {
    const match = /^#?(\w+(?:\.\w+)*)\.reduce\(\((\w+),\s*(\w+)\)\s*=>\s*/.exec(expr)
    if (!match) return undefined

    const sourcePath = match[1]
    const accName = match[2]
    const itemName = match[3]
    const rest = expr.substring(match[0].length)

    // Find the matching closing paren for .reduce(...) using depth tracking.
    // The match already consumed the opening "(" of reduce, so we start at depth 1.
    // We need to find the ")" that closes the reduce call, NOT the last ")" in the string.
    let depth = 1
    let reduceCloseIdx = -1
    for (let i = 0; i < rest.length; i++) {
      if (rest[i] === "(") depth++
      if (rest[i] === ")") {
        depth--
        if (depth === 0) {
          reduceCloseIdx = i
          break
        }
      }
    }
    if (reduceCloseIdx < 0) return undefined
    const inner = rest.substring(0, reduceCloseIdx)
    const suffix = rest.substring(reduceCloseIdx + 1).trim() // e.g. "* 100"

    const lastComma = findLastTopLevelComma(inner)
    if (lastComma < 0) return undefined

    const body = inner.substring(0, lastComma).trim()
    const initialStr = inner.substring(lastComma + 1).trim()

    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return toNum(this.evaluate(initialStr))

    let accumulator: unknown = this.evaluate(initialStr)

    for (const item of source) {
      const itemMapVal = item && typeof item === "object" ? item : {}
      accumulator = this.evalReduceBody(body, accName, accumulator, itemName, itemMapVal)
    }

    // Apply post-reduce arithmetic if present (e.g. "* 100", "/ 2")
    if (suffix) {
      return evalArithmetic(`${accumulator}${suffix}`)
    }
    return accumulator
  }

  private evalReduceBody(
    body: string,
    accName: string,
    accValue: unknown,
    itemName: string,
    itemMap: unknown,
  ): unknown {
    let processed = body

    // Replace item.field references with actual values
    const itemRe = new RegExp(`${itemName}\\.(\\w+)`, "g")
    processed = processed.replace(itemRe, (_, field) => {
      const val =
        itemMap && typeof itemMap === "object"
          ? (itemMap as Record<string, unknown>)[field]
          : null
      if (typeof val === "number") return val.toString()
      if (typeof val === "string") {
        const parsed = parseFloat(val)
        return isNaN(parsed) ? "0" : parsed.toString()
      }
      return "0"
    })

    // Replace accumulator reference
    processed = processed.split(accName).join(String(accValue))

    // Evaluate the resulting arithmetic expression
    return evalArithmetic(processed)
  }

  // ---- Map with Object Construction ----

  private tryMapExpression(expr: string): unknown {
    // Pattern 1: .map(x => ({...})) — object literal shorthand
    const match = /^#?(\w+(?:\.\w+)*)\.map\((\w+)\s*=>\s*\(\{/.exec(expr)
    if (match) {
      return this.evalMapObjectLiteral(expr, match[1], match[2])
    }

    // Pattern 1b: (source||[]).map(x => ({...})) — fallback array with object literal
    // Handles expressions like: (_results.groupsQuery.data||[]).map(g=>({label:g.field,value:g.id}))
    const fallbackObjMatch = /^\(#?([\w.]+)\|\|\[\]\)\.map\((\w+)\s*=>\s*\(\{/.exec(expr)
    if (fallbackObjMatch) {
      const sourcePath = fallbackObjMatch[1]
      const source = this.state.get(sourcePath)
      const resolvedSource = Array.isArray(source) ? source : []
      // Temporarily set the resolved source in state, eval, then restore
      const paramName = fallbackObjMatch[2]
      // Rewrite expr to simple form for evalMapObjectLiteral
      const simpleExpr = `${sourcePath}.map(${paramName}=>${expr.substring(expr.indexOf("=>(") + 2)}`
      // Store resolved array if source was null/undefined
      if (!Array.isArray(source)) {
        this.state.get(sourcePath) // no-op, source stays undefined
        // evalMapObjectLiteral will get [] from state.get, so return empty
        return resolvedSource.length === 0 ? [] : this.evalMapObjectLiteral(simpleExpr, sourcePath, paramName)
      }
      return this.evalMapObjectLiteral(simpleExpr, sourcePath, paramName)
    }

    // Pattern 2: .map(x => { const ...; return { ...x, key: val }; })
    // Block body with local variables and a return statement
    const blockMatch = /^#?(\w+(?:\.\w+)*)\.map\((\w+)\s*=>\s*\{/.exec(expr)
    if (blockMatch) {
      return this.evalMapBlockBody(expr, blockMatch[1], blockMatch[2])
    }

    // Pattern 2b: (source||[]).map(x => { ... }) — fallback array with block body
    const fallbackBlockMatch = /^\(#?([\w.]+)\|\|\[\]\)\.map\((\w+)\s*=>\s*\{/.exec(expr)
    if (fallbackBlockMatch) {
      const sourcePath = fallbackBlockMatch[1]
      const source = this.state.get(sourcePath)
      if (!Array.isArray(source)) return []
      const paramName = fallbackBlockMatch[2]
      const simpleExpr = `${sourcePath}.map(${paramName}=>${expr.substring(expr.indexOf("=>{") + 2)}`
      return this.evalMapBlockBody(simpleExpr, sourcePath, paramName)
    }

    return undefined
  }

  /** Evaluate .map(x => ({...})) — object literal shorthand */
  private evalMapObjectLiteral(expr: string, sourcePath: string, paramName: string): unknown[] {
    const bodyStart = expr.indexOf("({") + 2
    const bodyEnd = expr.lastIndexOf("})")
    if (bodyEnd < bodyStart) return []

    const objectBody = expr.substring(bodyStart, bodyEnd).trim()

    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return []

    return source.map((item) => {
      const itemMap = toStringKeyMap(item)
      const result: Record<string, unknown> = {}

      // Handle spread: ...paramName
      if (objectBody.includes(`...${paramName}`)) {
        Object.assign(result, itemMap)
      }

      // Parse key: expr pairs after spread
      const pairsStr = objectBody.replace(new RegExp(`\\.\\.\\.\\w+,?\\s*`), "")
      const pairs = splitObjectPairs(pairsStr)

      for (const pair of pairs) {
        const colonIdx = pair.indexOf(":")
        if (colonIdx < 0) continue
        const key = pair.substring(0, colonIdx).trim()
        const valueExpr = pair.substring(colonIdx + 1).trim()
        result[key] = this.evalMapItemExpr(valueExpr, paramName, itemMap)
      }

      return result
    })
  }

  /**
   * Evaluate .map(x => { ... }) — complex block body with const, if, find, etc.
   * Uses sandboxed Function() as fallback since pattern matching can't handle
   * arbitrary JS control flow (if/return, .find(), .trim(), etc.).
   */
  private evalMapBlockBody(expr: string, sourcePath: string, paramName: string): unknown[] {
    const source = this.state.get(sourcePath)
    if (!Array.isArray(source)) return []

    // Extract the full function body: "paramName => { ... }"
    // Strip the trailing ")" from .map(...) wrapper
    const arrowIdx = expr.indexOf(`${paramName} => `)
    if (arrowIdx < 0) return source
    let fnBody = expr.substring(arrowIdx)
    // Remove the closing paren of .map(...)
    if (fnBody.endsWith(")")) fnBody = fnBody.slice(0, -1)

    // Build a scope by resolving all dot-path references in the expression
    // through the SymbolStore (which handles namespace aliases).
    const scope: Record<string, unknown> = {}

    // Extract all identifier.path references from the expression
    const refs = new Set<string>()
    const refPattern = /(?<!\w)(\w+(?:\.\w+)+)(?!\w*\()/g
    let m: RegExpExecArray | null
    while ((m = refPattern.exec(fnBody)) !== null) {
      // Skip param.field references (those are map item properties)
      if (!m[1].startsWith(paramName + ".")) {
        refs.add(m[1])
      }
    }

    // For each dot-path reference, resolve through state and inject
    // as a nested object in the scope
    for (const ref of refs) {
      const val = this.state.get(ref)
      if (val !== undefined) {
        // Build nested scope: "ns.field" → scope.ns = { field: val }
        const parts = ref.split(".")
        let obj = scope
        for (let i = 0; i < parts.length - 1; i++) {
          if (!(parts[i] in obj) || typeof obj[parts[i]] !== "object") {
            obj[parts[i]] = {}
          }
          obj = obj[parts[i]] as Record<string, unknown>
        }
        obj[parts[parts.length - 1]] = val
      }
    }

    // Also add _results from state (commonly used in cross-step references)
    const results = this.state.get("_results")
    if (results !== undefined) scope["_results"] = results

    try {
      const scopeKeys = Object.keys(scope)
      const scopeValues = scopeKeys.map(k => scope[k])
      const mapFn = new Function(
        ...scopeKeys,
        `"use strict"; const mapperFn = ${fnBody}; return mapperFn;`,
      )(...scopeValues)

      return source.map(mapFn)
    } catch {
      // If Function() fails, return source array unchanged
      return source
    }
  }

  private evalMapItemExpr(
    expr: string,
    paramName: string,
    itemMap: Record<string, unknown>,
  ): unknown {
    const trimmed = expr.trim()

    // formatCurrency(item.expr)
    const currMatch = /^formatCurrency\((.+)\)$/.exec(trimmed)
    if (currMatch) {
      const inner = this.evalMapItemExpr(currMatch[1].trim(), paramName, itemMap)
      const val = toNum(inner)
      return `$${val.toFixed(2)}`
    }

    // Ternary: condition ? valA : valB
    const ternIdx = findTopLevelChar(trimmed, "?")
    if (ternIdx > 0) {
      const colonIdx = findTopLevelChar(trimmed.substring(ternIdx + 1), ":")
      if (colonIdx > 0) {
        const condition = trimmed.substring(0, ternIdx).trim()
        const trueVal = trimmed.substring(ternIdx + 1, ternIdx + 1 + colonIdx).trim()
        const falseVal = trimmed.substring(ternIdx + 1 + colonIdx + 1).trim()
        const condResult = this.evalMapItemCondition(condition, paramName, itemMap)
        return condResult
          ? this.evalMapItemExpr(trueVal, paramName, itemMap)
          : this.evalMapItemExpr(falseVal, paramName, itemMap)
      }
    }

    // Arithmetic: item.field * item.field2
    for (const op of ["*", "+", "-", "/"]) {
      const opIdx = findTopLevelChar(trimmed, op)
      if (opIdx > 0) {
        const left = this.evalMapItemExpr(trimmed.substring(0, opIdx).trim(), paramName, itemMap)
        const right = this.evalMapItemExpr(trimmed.substring(opIdx + 1).trim(), paramName, itemMap)
        const l = toNum(left)
        const r = toNum(right)
        switch (op) {
          case "*": return l * r
          case "+": return l + r
          case "-": return l - r
          case "/": return r !== 0 ? l / r : 0
        }
      }
    }

    // Item property: item.field
    if (trimmed.startsWith(`${paramName}.`)) {
      return getNestedValue(itemMap, trimmed.substring(paramName.length + 1))
    }

    // String literal
    if (
      (trimmed.startsWith("'") && trimmed.endsWith("'")) ||
      (trimmed.startsWith('"') && trimmed.endsWith('"'))
    ) {
      return trimmed.substring(1, trimmed.length - 1)
    }

    // Number literal
    const asNum = parseNumberStrict(trimmed)
    if (asNum !== null) return asNum

    // null literal
    if (trimmed === "null") return null

    // State reference
    if (trimmed.startsWith("#")) {
      return this.state.get(trimmed.substring(1))
    }

    return trimmed
  }

  private evalMapItemCondition(
    condition: string,
    paramName: string,
    itemMap: Record<string, unknown>,
  ): boolean {
    const trimmed = condition.trim()

    // item.field != null
    const neqNull = /^(\w+\.\w+)\s*!=\s*null$/.exec(trimmed)
    if (neqNull) {
      const val = this.resolveMapItemPath(neqNull[1], paramName, itemMap)
      return val != null
    }

    // Comparison operators
    for (const op of [">=", "<=", ">", "<", "==", "!="]) {
      const opIdx = trimmed.indexOf(op)
      if (opIdx > 0) {
        const left = this.evalMapItemExpr(trimmed.substring(0, opIdx).trim(), paramName, itemMap)
        const right = this.evalMapItemExpr(
          trimmed.substring(opIdx + op.length).trim(),
          paramName,
          itemMap,
        )
        return compareValues(left, right, op)
      }
    }

    // && and ||
    const andIdx = findTopLevelStr(trimmed, "&&")
    if (andIdx > 0) {
      return (
        this.evalMapItemCondition(trimmed.substring(0, andIdx).trim(), paramName, itemMap) &&
        this.evalMapItemCondition(trimmed.substring(andIdx + 2).trim(), paramName, itemMap)
      )
    }

    // Truthy check
    const val = this.evalMapItemExpr(trimmed, paramName, itemMap)
    return isTruthy(val)
  }

  private resolveMapItemPath(
    path: string,
    paramName: string,
    itemMap: Record<string, unknown>,
  ): unknown {
    if (path.startsWith(`${paramName}.`)) {
      return getNestedValue(itemMap, path.substring(paramName.length + 1))
    }
    if (path.startsWith("#")) return this.state.get(path.substring(1))
    return path
  }

  // ---- Array Index Access ----

  /**
   * Resolve expressions with bracket notation: path[0], path[0].property, path.sub[1].prop
   * Splits on brackets, resolves the base path, then applies index + remaining property access.
   */
  private resolveArrayAccess(expr: string): unknown {
    // Match: basePath[index] or basePath[index].rest
    const m = /^([^[]+)\[(\d+)\](.*)$/.exec(expr)
    if (!m) return undefined

    const basePath = m[1].trim().replace(/^#/, "")
    const index = parseInt(m[2], 10)
    let rest = m[3] // ".property" or "[1].property" or ""

    // Resolve the base array
    const arr = this.state.get(basePath)
    if (!Array.isArray(arr)) return undefined
    if (index < 0 || index >= arr.length) return undefined

    let value: unknown = arr[index]

    // Handle remaining path: .property.sub or [1].prop
    while (rest) {
      if (value == null) return undefined
      // Another bracket: [n]...
      const bracketM = /^\[(\d+)\](.*)$/.exec(rest)
      if (bracketM) {
        if (!Array.isArray(value)) return undefined
        const idx = parseInt(bracketM[1], 10)
        value = (value as unknown[])[idx]
        rest = bracketM[2]
        continue
      }
      // Dot property: .prop...
      const dotM = /^\.([a-zA-Z_]\w*)(.*)$/.exec(rest)
      if (dotM) {
        if (typeof value !== "object") return undefined
        value = (value as Record<string, unknown>)[dotM[1]]
        rest = dotM[2]
        continue
      }
      break
    }

    return value
  }

  // ---- Ternary ----

  private tryTernary(expr: string): unknown {
    const qIdx = findTopLevelChar(expr, "?")
    if (qIdx <= 0) return undefined

    const condition = expr.substring(0, qIdx).trim()
    const rest = expr.substring(qIdx + 1)
    const cIdx = findTopLevelChar(rest, ":")
    if (cIdx < 0) return undefined

    const trueExpr = rest.substring(0, cIdx).trim()
    const falseExpr = rest.substring(cIdx + 1).trim()

    const condVal = this.evaluate(condition)
    return isTruthy(condVal) ? this.evaluate(trueExpr) : this.evaluate(falseExpr)
  }

  // ---- Comparison ----

  private tryComparison(expr: string): unknown {
    // Check multi-char operators first (longest match), then single-char, all at top level (depth 0).
    // IMPORTANT: === and !== must come BEFORE == and != to avoid partial matching.
    for (const op of ["===", "!==", ">=", "<=", "!=", "==", ">", "<"]) {
      const idx = op.length > 1
        ? findTopLevelStr(expr, op)
        : findTopLevelChar(expr, op)
      if (idx > 0) {
        // Ensure single-char > or < isn't part of >= or <=
        if (op === ">" && idx + 1 < expr.length && expr[idx + 1] === "=") continue
        if (op === "<" && idx + 1 < expr.length && expr[idx + 1] === "=") continue

        const left = this.evaluate(expr.substring(0, idx).trim())
        const right = this.evaluate(expr.substring(idx + op.length).trim())
        return compareValues(left, right, op)
      }
    }
    return undefined
  }
}

// ---- Utility Functions ----

/** Check if a value is truthy (matches Flutter's CogoStateManager.isTruthy). */
export function isTruthy(val: unknown): boolean {
  if (val === null || val === undefined) return false
  if (typeof val === "boolean") return val
  if (typeof val === "number") return val !== 0
  if (typeof val === "string") return val.length > 0
  if (Array.isArray(val)) return val.length > 0
  return true
}

function compareValues(left: unknown, right: unknown, op: string): boolean {
  switch (op) {
    case "===":
    case "==":
      return left === right || String(left) === String(right)
    case "!==":
    case "!=":
      return left !== right && String(left) !== String(right)
    case ">":
      return typeof left === "number" && typeof right === "number" ? left > right : false
    case "<":
      return typeof left === "number" && typeof right === "number" ? left < right : false
    case ">=":
      return typeof left === "number" && typeof right === "number" ? left >= right : false
    case "<=":
      return typeof left === "number" && typeof right === "number" ? left <= right : false
  }
  return false
}

function compareNumbers(left: number, right: number, op: string): boolean {
  switch (op) {
    case "==": return left === right
    case "!=": return left !== right
    case ">": return left > right
    case "<": return left < right
    case ">=": return left >= right
    case "<=": return left <= right
  }
  return false
}

/**
 * Prefix bare identifiers in a condition with paramName.
 * e.g., 'status == "pending"' → '_$item.status == "pending"'
 * Skips #state refs, quoted strings, and numbers.
 */
function prefixBareFields(condition: string, paramName: string): string {
  // Replace bare identifiers (word chars, possibly dotted) that are:
  // - not already prefixed with #
  // - not a string literal
  // - not a number
  // - not a boolean/null literal
  // - not the paramName itself
  return condition.replace(
    /(?<![#"'.\w])([a-zA-Z_]\w*(?:\.\w+)*)(?=\s*(?:==|!=|>=|<=|>|<|&&|\|\||\)|$))/g,
    (match) => {
      // Skip literals, state refs, and already-prefixed
      if (match === "true" || match === "false" || match === "null") return match
      if (match.startsWith(paramName)) return match
      return `${paramName}.${match}`
    },
  )
}

/** Strip surrounding single or double quotes from a string. */
function stripQuotes(s: string): string {
  if (
    (s.startsWith("'") && s.endsWith("'")) ||
    (s.startsWith('"') && s.endsWith('"'))
  ) {
    return s.substring(1, s.length - 1)
  }
  return s
}

/** Convert unknown to number safely. */
export function toNum(v: unknown): number {
  if (typeof v === "number") return v
  if (typeof v === "string") {
    const n = parseFloat(v)
    return isNaN(n) ? 0 : n
  }
  return 0
}

function parseNumberStrict(s: string): number | null {
  if (s === "") return null
  // Match integer or float patterns
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    const n = Number(s)
    return isNaN(n) ? null : n
  }
  return null
}

function getNestedValue(obj: unknown, path: string): unknown {
  const keys = path.split(".")
  let current: unknown = obj
  for (const key of keys) {
    if (current == null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

function toStringKeyMap(item: unknown): Record<string, unknown> {
  if (!item || typeof item !== "object") return {}
  return item as Record<string, unknown>
}

/** Split comma-separated args respecting parentheses. */
export function splitArgs(s: string): string[] {
  const result: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "(") depth++
    if (s[i] === ")") depth--
    if (s[i] === "," && depth === 0) {
      result.push(s.substring(start, i))
      start = i + 1
    }
  }
  result.push(s.substring(start))
  return result
}

/** Split expression by a logical operator at top level (not inside parens). */
export function splitLogicalParts(expr: string, op: string): [string, string] | null {
  let depth = 0
  for (let i = 0; i <= expr.length - op.length; i++) {
    if (expr[i] === "(") depth++
    if (expr[i] === ")") depth--
    if (depth === 0 && expr.substring(i, i + op.length) === op) {
      return [expr.substring(0, i).trim(), expr.substring(i + op.length).trim()]
    }
  }
  return null
}

/** Check if outer parens match. */
export function matchingParen(expr: string): boolean {
  let depth = 0
  for (let i = 0; i < expr.length; i++) {
    if (expr[i] === "(") depth++
    if (expr[i] === ")") depth--
    if (depth === 0 && i < expr.length - 1) return false
  }
  return depth === 0
}

/** Find a character at top level (not inside parens/braces/quotes). */
function findTopLevelChar(s: string, ch: string): number {
  let depth = 0
  let inSingleQuote = false
  let inDoubleQuote = false
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "'" && !inDoubleQuote) inSingleQuote = !inSingleQuote
    if (s[i] === '"' && !inSingleQuote) inDoubleQuote = !inDoubleQuote
    if (inSingleQuote || inDoubleQuote) continue
    if (s[i] === "(" || s[i] === "{") depth++
    if (s[i] === ")" || s[i] === "}") depth--
    if (depth === 0 && s[i] === ch) return i
  }
  return -1
}

/** Find the LAST occurrence of a single-char operator at top level (depth 0).
 *  Right-to-left scan ensures correct operator precedence.
 *  Respects string literals (single/double quotes) to avoid matching inside strings. */
function findLastTopLevelChar(s: string, ch: string): number {
  let depth = 0
  let inSingle = false
  let inDouble = false
  for (let i = s.length - 1; i > 0; i--) {
    if (s[i] === "'" && !inDouble) inSingle = !inSingle
    if (s[i] === '"' && !inSingle) inDouble = !inDouble
    if (inSingle || inDouble) continue
    if (s[i] === ")" || s[i] === "}") depth++
    if (s[i] === "(" || s[i] === "{") depth--
    if (depth === 0 && s[i] === ch) return i
  }
  return -1
}

/** Find a multi-char string at top level (respects string literals). */
function findTopLevelStr(s: string, target: string): number {
  let depth = 0
  let inSingle = false
  let inDouble = false
  for (let i = 0; i <= s.length - target.length; i++) {
    if (s[i] === "'" && !inDouble) { inSingle = !inSingle; continue }
    if (s[i] === '"' && !inSingle) { inDouble = !inDouble; continue }
    if (inSingle || inDouble) continue
    if (s[i] === "(" || s[i] === "{") depth++
    if (s[i] === ")" || s[i] === "}") depth--
    if (depth === 0 && s.substring(i, i + target.length) === target) return i
  }
  return -1
}

/** Split a string on top-level commas, respecting nested brackets and parens. Returns null if unbalanced. */
function splitTopLevelCommas(s: string): string[] | null {
  const parts: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]
    if (ch === "(" || ch === "[" || ch === "{") depth++
    else if (ch === ")" || ch === "]" || ch === "}") depth--
    else if (ch === "," && depth === 0) {
      parts.push(s.substring(start, i))
      start = i + 1
    }
    if (depth < 0) return null // unbalanced
  }
  parts.push(s.substring(start))
  return parts
}

function findLastTopLevelComma(s: string): number {
  let depth = 0
  let braceDepth = 0
  let lastComma = -1
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "(") depth++
    if (s[i] === ")") depth--
    if (s[i] === "{") braceDepth++
    if (s[i] === "}") braceDepth--
    if (s[i] === "," && depth === 0 && braceDepth === 0) {
      lastComma = i
    }
  }
  return lastComma
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

function splitObjectPairs(s: string): string[] {
  const result: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "(" || s[i] === "{") depth++
    if (s[i] === ")" || s[i] === "}") depth--
    if (s[i] === "," && depth === 0) {
      const part = s.substring(start, i).trim()
      if (part.length > 0) result.push(part)
      start = i + 1
    }
  }
  const last = s.substring(start).trim()
  if (last.length > 0) result.push(last)
  return result
}

/**
 * Simple arithmetic evaluator for reduce bodies.
 * Uses recursive descent: lowest-precedence operators (+/-) first,
 * then higher-precedence (* /) so `a + b * c` evaluates correctly.
 */
function evalArithmetic(expr: string): number {
  const trimmed = expr.trim()

  const asNum = parseFloat(trimmed)
  if (!isNaN(asNum) && /^-?\d+(\.\d+)?$/.test(trimmed)) return asNum

  // Handle addition/subtraction first (lowest precedence)
  // Find last top-level + or -
  for (let i = trimmed.length - 1; i > 0; i--) {
    if ((trimmed[i] === "+" || trimmed[i] === "-") && isTopLevel(trimmed, i)) {
      const left = evalArithmetic(trimmed.substring(0, i))
      const right = evalArithmetic(trimmed.substring(i + 1))
      return trimmed[i] === "+" ? left + right : left - right
    }
  }

  // Handle multiplication/division (higher precedence)
  for (let i = trimmed.length - 1; i > 0; i--) {
    if ((trimmed[i] === "*" || trimmed[i] === "/") && isTopLevel(trimmed, i)) {
      const left = evalArithmetic(trimmed.substring(0, i))
      const right = evalArithmetic(trimmed.substring(i + 1))
      return trimmed[i] === "*" ? left * right : (right !== 0 ? left / right : 0)
    }
  }

  // Handle parens
  if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
    return evalArithmetic(trimmed.substring(1, trimmed.length - 1))
  }

  const fallback = parseFloat(trimmed)
  return isNaN(fallback) ? 0 : fallback
}

function isTopLevel(s: string, idx: number): boolean {
  let depth = 0
  for (let i = 0; i < idx; i++) {
    if (s[i] === "(") depth++
    if (s[i] === ")") depth--
  }
  return depth === 0
}

/** Format a Date using simple pattern tokens: YYYY, MM, DD */
function formatDatePattern(d: Date, fmt: string): string {
  const yyyy = String(d.getFullYear())
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return fmt.replace("YYYY", yyyy).replace("MM", mm).replace("DD", dd)
}

/** Find a multi-char operator (||, ??, etc.) at top level (not inside parens/quotes). */
function findTopLevelOp(s: string, op: string): number {
  let depth = 0
  let inSingle = false
  let inDouble = false
  for (let i = 0; i < s.length - (op.length - 1); i++) {
    const ch = s[i]
    if (ch === "'" && !inDouble) inSingle = !inSingle
    else if (ch === '"' && !inSingle) inDouble = !inDouble
    else if (!inSingle && !inDouble) {
      if (ch === "(" || ch === "{" || ch === "[") depth++
      else if (ch === ")" || ch === "}" || ch === "]") depth--
      else if (depth === 0 && s.slice(i, i + op.length) === op) return i
    }
  }
  return -1
}
